"use client";

/**
 * "Is this your property?" - the owner-loop hook on the analyze result.
 *
 * Analyzing a listing is the one moment we hold every valuation input
 * for a property; this card turns that into a tracked home in two
 * taps by deep-linking /properties/new with the full prefill the add
 * form already understands (formFromParams / locationFromParams).
 * Hidden for rent analyses (tracking targets owned homes).
 */
import { Building2, ChevronRight } from "lucide-react";
import Link from "@/lib/localized-link";
import { useT } from "@/lib/locale-context";

export default function TrackHomeCta(props: {
  property_type?: string | null;
  area_m2?: number | null;
  room_count?: number | null;
  floor?: number | null;
  total_floors?: number | null;
  land_area_sot?: number | null;
  location_name?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  category_name?: string | null;
  has_repair?: boolean | null;
  has_bill_of_sale?: boolean | null;
}) {
  const t = useT();

  const q = new URLSearchParams();
  const set = (k: string, v: unknown) => {
    if (v !== null && v !== undefined && v !== "") q.set(k, String(v));
  };
  set("property_type", props.property_type === "house" ? "house" : "apartment");
  set("area_m2", props.area_m2);
  set("room_count", props.room_count);
  set("floor", props.floor);
  set("total_floors", props.total_floors);
  set("land_area_sot", props.land_area_sot);
  set("location_name", props.location_name);
  set("name", props.location_name);
  set("lat", props.latitude);
  set("lng", props.longitude);
  set("building_type", props.category_name);
  if (props.has_repair) q.set("has_repair", "1");
  if (props.has_bill_of_sale) q.set("has_bill_of_sale", "1");

  // A quiet row, not a branded panel. The gradient card with its
  // decorative blob and white pill was the most AI-looking surface on
  // the result page and the owner asked for it to go (2026-09-22:
  // "it's looking really AI-ish, can you please dislodge it"). The
  // entry point itself stays: this is the one moment we hold every
  // input the tracker needs.
  return (
    <Link
      href={`/properties/new?${q.toString()}`}
      className="group flex items-center justify-between gap-3 rounded-2xl border border-gray-200 bg-white px-4 py-3 transition-colors hover:border-gray-300"
    >
      <span className="flex min-w-0 items-center gap-2.5">
        <Building2 className="h-4 w-4 shrink-0 text-gray-400 transition-colors group-hover:text-gray-700" />
        <span className="min-w-0">
          <span className="block text-[13px] font-semibold text-gray-900">{t("seller.owncard.title")}</span>
          <span className="block truncate text-[11px] text-gray-500">{t("seller.owncard.cta")}</span>
        </span>
      </span>
      <ChevronRight className="h-4 w-4 shrink-0 text-gray-300 transition-colors group-hover:text-gray-700" />
    </Link>
  );
}
